import { apm } from '@elastic/apm-rum';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Redirect, Route, Switch, matchPath, useHistory, useLocation } from 'react-router-dom';
import LoadingPage from '../../components/LoadingPage/LoadingPage';
import ProductAlreadyAdded from '../../components/Product/ProductAlreadyAdded';
import DownloadMediasNotifier from '../../components/Snackbar/DownloadMediasNotifier';
import { UserActivity } from '../../components/UserActivity';
import AppTemplate from '../../components/v2/layout/AppTemplate/AppTemplate';
import useAuthenticationInterceptor from '../../hooks/useAuthenticationInterceptor';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import useHandleApiError from '../../hooks/useHandleApiError';
import { useUserContextPreferences } from '../../hooks/useUserContextPreferences';
import LangEnum from '../../lang/LangEnum';
import SecureRoute from '../../lib/SecureRoute';
import AnnouncementBarContainer from '../../modules/announcements/AnnouncementBarContainer';
import GlobalCatalogCreationModal from '../../modules/catalogs/creation/GlobalCatalogCreationModal';
import ChatHandler from '../../modules/chat/ChatHandler';
import ChatProvidersBundle from '../../modules/chat/contexts/ChatProvidersBundle';
import {
  ApplicationContextProvider,
  useApplicationContext,
} from '../../modules/context/ApplicationContext/ApplicationContext';
import { Brand } from '../../modules/context/ApplicationContext/applicationContext.types';
import { useAuthContext } from '../../modules/login/AuthContext';
import NpsContainer from '../../modules/net-promote-score/NpsContainer';
import { useAutoOfflineDataUpdate } from '../../modules/offline/hooks/useAutoOfflineDataUpdate/useAutoOfflineDataUpdate';
import { useLogOfflineOrders } from '../../modules/offline/hooks/useLogOfflineOrders';
import useOrderBook from '../../modules/order-book/hooks/useOrderBook';
import { OrderView } from '../../modules/orders/ordersView.types';
import { FeatureVisibilityProvider } from '../../modules/permissions/contexts/FeatureVisibilityContext';
import ProductDetailsPanel from '../../modules/products/products-details-v2/components/ProductDetailsPanel/ProductDetailsPanel';
import ProductDetailsUrlSync from '../../modules/products/products-details-v2/components/ProductDetailsUrlSync/ProductDetailsUrlSync';
import RachelChatProvider from '../../modules/rachel/components/RachelChatProvider/RachelChatProvider';
import RachelPanel from '../../modules/rachel/components/RachelPanel/RachelPanel';
import SsoReauthWarningContainer from '../../modules/sso/components/SsoReauthWarningContainer';
import PosContainer from '../../modules/store-point-of-sale/PosContainer';
import { useTranslation } from '../../modules/translation/TranslationContext';
import { useAbility } from '../../modules/users/AbilityContext';
import api, { restApi } from '../../services/api';
import useGeneralSettingsStore from '../../stores/GeneralSettings/useGeneralSettingsStore';
import snackbarStore from '../../stores/Snackbar.store';
import useSystemParametersStore from '../../stores/SystemParameters/useSystemParameters.store';
import { BaseErrorResponseData, TeceoApiResponse } from '../../types/request.types';
import useHomeRoutes from '../home.routes';
import PATHS from '../routes.const';
import HomeNavigationBottomBar from './components/HomeNavigationBottomBar/HomeNavigationBottomBar';
import HomeNavigationHeader from './components/HomeNavigationHeader/HomeNavigationHeader';
import HomeNavigationSidebarBottom from './components/HomeNavigationSidebarBottom/HomeNavigationSidebarBottom';
import SettingsMenuSearch from './components/SettingsMenuSearch/SettingsMenuSearch';
import { useAppTemplateMode } from './hooks/useAppTemplateMode';
import { useHomeNavItems } from './hooks/useHomeNavItems';
import { useSettingsMenuSearch } from './hooks/useSettingsMenuSearch';
import { useSubNavContext } from './hooks/useSubNavContext';

const shouldVerifyAccess = (basePath: string, id: string, isStore: boolean) => {
  const shouldVerify = ['product', 'catalogs'].includes(basePath) && !!id && isStore;

  return shouldVerify;
};

const buildVerifyAccessUrl = (basePath: string, id: string) =>
  basePath === 'product' ? `${basePath}s/${id}/verify-access` : `${basePath}/${id}/verify-access`;

const handleVerifyAccessResponse = (
  response: TeceoApiResponse<{ brandId: string }>,
  brands: Brand[],
  changeBrand: (brand: Brand) => void,
) => {
  if (response.ok) {
    const brand = brands.find((stateBrand) => stateBrand.id === response.data?.brandId);

    if (brand) {
      changeBrand(brand);
    }
  }
};

const Home = () => {
  useUserContextPreferences();

  const location = useLocation();
  const history = useHistory();
  const ability = useAbility();

  const context = useAuthContext();
  const { fetchGeneralSettings } = useGeneralSettingsStore();
  const { initializeOrderBookStore } = useOrderBook();

  const { state, isStore, userRole, team, changeBrand, isBrand } = useApplicationContext();
  const homeRoutes = useHomeRoutes();

  const { navItems, subContextNavItems } = useHomeNavItems();
  const { contextMode, collapsedMode, onBack } = useAppTemplateMode();

  useLogOfflineOrders();
  useAutoOfflineDataUpdate();

  const { handleMessage } = snackbarStore;
  const { pathname, search } = location;

  const isSettingsContext = useSubNavContext(pathname) === 'settings';
  const { searchTerm, setSearchTerm, filteredSections, hasNoResults } = useSettingsMenuSearch({
    sections: subContextNavItems,
    isEnabled: isSettingsContext,
  });

  const searchParams = new URLSearchParams(search);
  const redirectPathOrPathname = searchParams.get('redirectPath') || pathname;
  const { isEnableNps, isEnablePos } = useFeatureFlag();

  const [basePath, id] = redirectPathOrPathname.split('/').filter((item) => item !== '');
  const {
    fetchBrandSystemParameters,
    fetchGlobalSystemParameters,
    fetchStoreSystemParameters,
    fetchStoreBrandsAggregatedSystemParameters,
  } = useSystemParametersStore();

  const [isVerifying, setIsVerifying] = useState(false);
  const { translate, isMessagesLoaded } = useTranslation();
  const { getApiErrorMessage } = useHandleApiError();

  const mustShowPos =
    isEnablePos && !!team?.shouldShowPos && ability.can('update', 'store-points-of-sale');

  const mustShowNps =
    isEnableNps &&
    !team?.shouldShowPos &&
    !!team?.shouldShowNps &&
    ability.can('update', 'net-promoter-score');

  const onAuthenticationError = async () => {
    api.resetSessionHeaders();
    await context.logout();
  };

  const onForbiddenError = (error?: BaseErrorResponseData) => {
    const isAbilityError = error?.message?.startsWith('Access denied to');

    history.replace('/');
    handleMessage({
      type: 'error',
      message: isAbilityError
        ? translate(LangEnum.SNACKBAR_ACCESS_DENIED)
        : getApiErrorMessage(
            { ok: false, data: error },
            translate(LangEnum.SNACKBAR_ACCESS_DENIED),
          ),
    });
  };

  useAuthenticationInterceptor({ onLogout: onAuthenticationError, onForbiddenError });

  const userInLocalStorage = localStorage.user;
  const localUser = useMemo(
    () => (state?.user && userInLocalStorage ? JSON.parse(userInLocalStorage) : undefined),
    [state?.user, userInLocalStorage],
  );

  useEffect(() => {
    if (team?.id) {
      initializeOrderBookStore();
    }
  }, [team?.id, state.user]);

  useEffect(() => {
    if (state.brand || isBrand || state.user?.brand) {
      fetchGeneralSettings();
      fetchBrandSystemParameters();
    }
  }, [state?.user, fetchBrandSystemParameters, fetchGeneralSettings, state.brand]);

  useEffect(() => {
    fetchGlobalSystemParameters();
  }, [fetchGlobalSystemParameters]);

  useEffect(() => {
    if (isStore && state.user?.store?.id) {
      fetchStoreSystemParameters();
      fetchStoreBrandsAggregatedSystemParameters();
    }
  }, [
    isStore,
    state.user?.store?.id,
    fetchStoreSystemParameters,
    fetchStoreBrandsAggregatedSystemParameters,
  ]);

  const brandLoading: boolean = useMemo(
    () => localUser?.brand && localUser?.brand !== (state.brand?.id || team?.brand?.id),
    [team, localUser?.brand, state.brand],
  );

  const shouldShowLoading =
    !ability ||
    ability.isLoadingAbilities ||
    state.isLoadingBrands ||
    brandLoading ||
    !userRole ||
    isVerifying ||
    !isMessagesLoaded;

  const verifyAccess = useCallback(async () => {
    try {
      if (shouldVerifyAccess(basePath, id, isStore)) {
        setIsVerifying(true);
      }

      if (!state.isLoadingBrands && shouldVerifyAccess(basePath, id, isStore)) {
        const verifyAccessUrl = buildVerifyAccessUrl(basePath, id);

        const response: TeceoApiResponse<{ brandId: string }> = await restApi.get(verifyAccessUrl);

        handleVerifyAccessResponse(response, state.brands, changeBrand);
      }
    } catch (error: any) {
      apm.captureError(error);
    } finally {
      setIsVerifying(false);
    }
  }, [changeBrand, isStore, state.brands, state.isLoadingBrands]);

  useEffect(() => {
    verifyAccess();
  }, [verifyAccess]);

  if (shouldShowLoading) {
    return <LoadingPage />;
  }

  return (
    <RachelChatProvider>
      {mustShowPos && <PosContainer />}
      {mustShowNps && <NpsContainer />}
      <GlobalCatalogCreationModal />
      <SsoReauthWarningContainer />
      <ProductDetailsUrlSync />

      <AppTemplate
        navItems={navItems}
        subContextNavItems={filteredSections}
        collapsedMode={collapsedMode}
        contextMode={contextMode}
        onBack={onBack}
        slots={{
          topSlot: <AnnouncementBarContainer />,
          header: <HomeNavigationHeader />,
          bottomBar: <HomeNavigationBottomBar />,
          sidebarBottom: <HomeNavigationSidebarBottom />,
          subContextTop: isSettingsContext ? (
            <SettingsMenuSearch
              value={searchTerm}
              hasNoResults={hasNoResults}
              onValueChange={setSearchTerm}
            />
          ) : undefined,
          workspaceOverlay: (
            <>
              <ProductDetailsPanel />
              <RachelPanel />
            </>
          ),
        }}
      >
        <Switch>
          <Route
            exact
            path={PATHS.main.orders.editForm.redirect}
            render={() =>
              matchPath(pathname, PATHS.main.orders.editForm.redirect) ? (
                <Redirect
                  to={`${pathname}${
                    pathname.endsWith('/') ? OrderView.EDIT : `/${OrderView.EDIT}`
                  }`}
                />
              ) : null
            }
          />
          {homeRoutes.map((route) => (
            <SecureRoute {...route} key={route.path} />
          ))}

          <Redirect to="/" />
        </Switch>
      </AppTemplate>

      <ProductAlreadyAdded />
      <DownloadMediasNotifier />

      {ability.can('read', 'chats') && <ChatHandler />}
    </RachelChatProvider>
  );
};

const HomeNavigation = () => {
  return (
    <ApplicationContextProvider>
      <FeatureVisibilityProvider>
        <ChatProvidersBundle>
          <Home />
        </ChatProvidersBundle>
        <UserActivity />
      </FeatureVisibilityProvider>
    </ApplicationContextProvider>
  );
};

export default HomeNavigation;
